"use client";

import { Trans } from "@lingui/react/macro";

import { MAX_BOT_NAME_LENGTH } from "@/lib/bot-name";

interface HireNameFieldProps {
  /** Unique input id — the two call sites render on different screens. */
  id: string;
  /** The bot's display name — pre-filled, editable. */
  botName: string;
  /** Called as the user edits the name. */
  onNameChange: (name: string) => void;
  /** Availability message for the current name (null = available). */
  nameError: string | null;
  /** True while the availability check is in flight. */
  checkingName: boolean;
  /** True while the hire is in flight — the field locks. */
  disabled?: boolean;
  autoFocus?: boolean;
  /** Enter pressed while the name is submittable. */
  onEnter?: () => void;
}

/**
 * Whether a hire may be submitted with the current name state. Both naming
 * screens gate their confirm button on this: an in-flight hire, a check still
 * running, a known-bad name, or an empty field all block. Shared so the two
 * arms of the onboarding experiment can't drift on the rule that stops a hire
 * going out on an unverified name.
 */
export const nameSubmitBlocked = (
  { botName, nameError, checkingName, busy }:
  { botName: string; nameError: string | null; checkingName: boolean; busy: boolean },
): boolean => busy || checkingName || !!nameError || !botName.trim();

/**
 * The "name your Hire" field: label, input, and the live availability line
 * underneath. Rendered by both naming screens — the discovery arm's hand-off
 * card and the skip arm's name dialog — so the two read identically and a copy
 * change lands once.
 *
 * The availability state is owned by the caller (one debounced check in
 * onboarding-flow serves both screens); this component only renders it.
 */
export function HireNameField({
  id, botName, onNameChange, nameError, checkingName, disabled = false, autoFocus = false, onEnter,
}: HireNameFieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs text-foreground-muted"><Trans>Name your Hire</Trans></label>
      <input
        id={id}
        value={botName}
        onChange={(e) => { onNameChange(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
        maxLength={MAX_BOT_NAME_LENGTH}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-invalid={nameError ? true : undefined}
        className={`w-full rounded-xl border bg-surface px-3 py-2 text-foreground outline-none focus:ring-2 transition disabled:opacity-60 ${nameError ? "border-destructive focus:border-destructive focus:ring-destructive/30" : "border-border focus:border-primary/60 focus:ring-primary/30"}`}
      />
      <p className={`text-xs min-h-[1rem] ${nameError ? "text-destructive" : "text-foreground-muted"}`}>
        {checkingName ? <Trans>Checking…</Trans> : nameError ? nameError : <Trans>Available</Trans>}
      </p>
    </div>
  );
}
